import {
  Breadcrumb,
  Button,
  LoadingSpinner,
  ModalChangeStatusConfirm,
} from "@/components";
import { useShopStore } from "@/store/shopStore";
import { formatCodeNumber } from "@/utils/formatCodeNumber";
import { useEffect, useMemo, useState } from "react";
import { HiArrowLeft } from "react-icons/hi";
import { useNavigate, useParams } from "react-router-dom";
import { isNaN } from "lodash";
import { EditStore } from "../Company Details/EditStore";
import { UsersInStore } from "./components/UsersInStore";
import { useAuth } from "@/contexts/AuthContext";
import { ROUTES } from "@/constants/routes";
import {
  ECOMPANY_BUSINESS,
  PriorityRole,
  RoleEnum,
  RoleManagerExcute,
} from "@/types";

const DEFAULT_VALUE = "";

export default function StoreDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { fetchStore, fetchMyStore, shop, updateStoreStatus } = useShopStore();

  const storeStatus = useMemo(() => {
    return shop?.status;
  }, [shop]);

  const companyId = useMemo(() => {
    return shop?.company_id;
  }, [shop]);

  const companyName = useMemo(() => {
    return shop?.company?.name;
  }, [shop]);

  const storeDisplayName = useMemo(() => {
    return shop?.company?.store_display_name || "店舗";
  }, [shop]);

  // company have special business
  const isCompanySpecialBusiness = [
    ECOMPANY_BUSINESS.TS_RETAIL,
    ECOMPANY_BUSINESS.CS_RETAIL,
  ].includes(shop?.company?.business as ECOMPANY_BUSINESS);

  const getStoreInfo = async (id: number) => {
    setLoading(true);
    const funcFetch =
      role !== RoleEnum.STORE_MANAGER ? fetchStore : fetchMyStore;
    await funcFetch(id);
    setLoading(false);
  };

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
  };

  const changeStatusStore = async () => {
    updateStoreStatus(shop?.id || 0);
    closeModal();
  };

  useEffect(() => {
    if (!id || isNaN(Number(id))) {
      navigate(ROUTES.ERROR_404);
      return;
    }
    getStoreInfo(+id);
  }, [id]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-full w-full">
        <LoadingSpinner />
      </div>
    );

  return (
    <div className="h-full flex flex-col pb-4">
      {/* header */}
      <section className="space-y-2 py-4">
        {[RoleEnum.COMPANY_ADMIN, RoleEnum.HEAD_OFFICE_USER].includes(
          role || RoleEnum.OTHER
        ) && (
          <Breadcrumb
            breadcrumbs={[
              { label: "事業所詳細", path: "/company" },
              { label: `${storeDisplayName}詳細`, path: `/store/${id}` },
            ]}
          />
        )}

        {[RoleEnum.COMPANY_ADMIN, RoleEnum.HEAD_OFFICE_USER].includes(
          role || RoleEnum.OTHER
        ) ? (
          <button
            className="flex items-center gap-2 text-base font-bold leading-7 hover:text-cyan"
            onClick={() => navigate(-1)}
          >
            <HiArrowLeft className="w-5 h-5 hover:text-cyan" />{" "}
            {shop?.name || DEFAULT_VALUE}
          </button>
        ) : (
          <p className="text-base font-bold leading-7">
            {shop?.name || DEFAULT_VALUE}
          </p>
        )}
      </section>

      {/* main */}
      <section className="grid grid-cols-3 gap-4 flex-1">
        <div className="col-span-1 shadow-[0_0_2px_0_rgba(145,158,171,0.20),0_12px_24px_-4px_rgba(145,158,171,0.12)] pb-4 flex flex-col">
          <p className="text-base font-semibold leading-6 p-4  border-b border-gray-200">
            {storeDisplayName}情報
          </p>
          <div className="p-2">
            {[
              { label: `${storeDisplayName}名`, value: shop?.name },
              { label: "住所", value: shop?.address },
              {
                label: "ユーザー名",
                value: shop?.user?.[0]?.full_name || DEFAULT_VALUE,
              },
              {
                label: "電話番号",
                value: formatCodeNumber(shop?.phone || "") || DEFAULT_VALUE,
              },
              {
                label: "申告履歴",
                value: shop?.report_history || DEFAULT_VALUE,
              },
              {
                label: "随時点検履歴",
                value: shop?.store_inspection_history || DEFAULT_VALUE,
              },
            ].map((item) => (
              <div
                key={item.label}
                className="p-2 flex items-center justify-between gap-2  border-b border-gray-200"
              >
                <p className="text-sm font-semibold leading-5.5 min-w-[22%]">
                  {item.label}
                </p>
                <p className="text-sm font-semibold leading-5.5 text-auro_metal_saurus text-right">
                  {item.value}
                </p>
              </div>
            ))}
          </div>
          <div className="flex-1 items-end flex gap-2 px-4 w-full">
            {RoleManagerExcute.STORE.UPDATE[role || RoleEnum.OTHER] &&
              !isCompanySpecialBusiness && (
                <EditStore
                  id={shop?.id || 0}
                  className="w-full"
                  disabled={
                    PriorityRole[role || RoleEnum.OTHER] <
                      PriorityRole[RoleEnum.STORE_MANAGER] ||
                    (PriorityRole[role || RoleEnum.OTHER] ===
                      PriorityRole[RoleEnum.STORE_MANAGER] &&
                      shop?.status === "DISABLE")
                  }
                >
                  <Button variant="outlined" color="primary" className="w-full">
                    情報変更
                  </Button>
                </EditStore>
              )}
            {RoleManagerExcute.STORE.ACTIVE[role || RoleEnum.OTHER] &&
              !isCompanySpecialBusiness && (
                <div className="w-full">
                  {shop?.status == "ENABLE" ? (
                    <Button
                      className="!w-full text-error_main"
                      onClick={openModal}
                    >
                      無効にする
                    </Button>
                  ) : (
                    <Button
                      variant="solid"
                      color="primary"
                      className="!w-full"
                      onClick={openModal}
                    >
                      有効にする
                    </Button>
                  )}
                </div>
              )}
          </div>
        </div>
        <div className="col-span-2">
          <UsersInStore
            companyId={companyId}
            companyName={companyName}
            storeStatus={storeStatus}
          />
        </div>
      </section>

      <ModalChangeStatusConfirm
        open={open}
        setOpen={setOpen}
        currentStatus={shop?.status}
        onChangeStatus={changeStatusStore}
        onCancel={closeModal}
        storeDisplayName={storeDisplayName}
      />
    </div>
  );
}
